import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { getEvalRoleLabelLegacy } from '../utils/evalRoleDisplay';
import { useAuthStore } from '../store/auth';

export interface EvalRoleRow {
  id: number;
  code: string;
  displayName: string;
  enabled: boolean;
  isSystem: boolean;
}

interface EvalRoleLabelsContextValue {
  roles: EvalRoleRow[];
  enabledCodes: string[];
  /** 租户字典 displayName 优先，fallback 到 legacy 池内称呼，再 fallback code */
  getEvalRoleLabel: (templatePool: string | undefined, role: string | null | undefined) => string;
}

const EvalRoleLabelsContext = createContext<EvalRoleLabelsContextValue | null>(null);

export function EvalRoleLabelsProvider({ children }: { children: ReactNode }) {
  const profile = useAuthStore((s) => s.profile);
  const isLoggedIn = !!profile;

  const { data: roles = [] } = useQuery<EvalRoleRow[]>({
    queryKey: ['eval-roles'],
    queryFn: () => api.get('/admin/eval-roles') as Promise<EvalRoleRow[]>,
    staleTime: 60_000,
    enabled: isLoggedIn,
  });

  const labelByCode = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.code, r.displayName])),
    [roles],
  );

  const enabledCodes = useMemo(
    () => roles.filter((r) => r.enabled).map((r) => r.code),
    [roles],
  );

  const getEvalRoleLabel = useCallback(
    (templatePool: string | undefined, role: string | null | undefined) => {
      if (!role) return '-';
      if (labelByCode[role]) return labelByCode[role];
      return getEvalRoleLabelLegacy(templatePool, role);
    },
    [labelByCode],
  );

  const value = useMemo(
    () => ({ roles, enabledCodes, getEvalRoleLabel }),
    [roles, enabledCodes, getEvalRoleLabel],
  );

  return (
    <EvalRoleLabelsContext.Provider value={value}>{children}</EvalRoleLabelsContext.Provider>
  );
}

/** 非 admin 页或未包 Provider 时 fallback legacy 显示。 */
export function useEvalRoleLabels(): EvalRoleLabelsContextValue {
  const ctx = useContext(EvalRoleLabelsContext);
  const fallbackGet = useCallback(
    (templatePool: string | undefined, role: string | null | undefined) =>
      getEvalRoleLabelLegacy(templatePool, role),
    [],
  );
  return (
    ctx ?? {
      roles: [],
      enabledCodes: [],
      getEvalRoleLabel: fallbackGet,
    }
  );
}
